import { CommonModule } from '@angular/common';
import { InterviewRuntimeInstructions } from '@edtech-ui/constants/interview-language.constant';
import { Component, ElementRef, ViewChild, OnDestroy, Input, NgZone, ChangeDetectorRef, HostListener } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { firstValueFrom, retry, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { WebrtcService } from '../../services/WebrtcService';
import { AiInterviewSessionService } from '../../services/AiInterviewSessionService';
import { S3MultipartUploaderService } from '../../../../core/services/s3-multipart-uploader.service';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ProctoringService, ViolationState } from '../../services/proctoring.service';
import { ProctoringViolationType, ViolationSeverity } from '../../models/proctoring.models';
import { ProctoringWarningComponent } from '../../proctoring/warning/proctoring-warning.component';
import { ProctoringTerminatedComponent } from '../../proctoring/terminated/proctoring-terminated.component';
import { ProctoringConsentComponent } from '../../proctoring/consent/proctoring-consent.component';
import { ConnectivityService } from '../../services/connectivity.service';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Loader } from '../../components/loader/loader';
import { ScreenShareRequiredComponent } from '../../proctoring/screen-share-required/screen-share-required.component';

type ChatTurn = { role: 'assistant' | 'user'; content: string; ts: string };

@Component({
  standalone: true,
  selector: 'app-interview-panel',
  imports: [CommonModule, ReactiveFormsModule, FormsModule, ProctoringWarningComponent, ProctoringConsentComponent, MatDialogModule, ScreenShareRequiredComponent, Loader],
  templateUrl: './interview-panel.html',
  styleUrl: './interview-panel.css'

})
export class InterviewPanel {
  private chunkBuffer: Blob[] = [];
  private currentBufferSize: number = 0;
  private readonly CHUNK_SIZE_THRESHOLD = 10 * 1024 * 1024; // 10MB
  private uploadId: string | null = null;
  private objectKey: string | null = null;
  private uploadedParts: any[] = [];
  private partNumber: number = 1;
  private isFlushing: boolean = false;
  private flushQueue: Promise<void> = Promise.resolve();

  @ViewChild('videoElement', { static: false }) videoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild('remoteAudio', { static: false }) remoteAudio!: ElementRef<HTMLAudioElement>;
  @ViewChild('snapshotVideo', { static: false }) snapshotVideo!: ElementRef<HTMLVideoElement>;
  @Input() isNavigatingFromWizard = false;
  @ViewChild('chatWindow') chatWindow!: ElementRef<HTMLDivElement>;
  transcript: string[] = [];
  chatLog: ChatTurn[] = [];
  isConnected = false;
  timeRemaining = '00:00';
  isAnalyzing = false;
  showLeaveDialog = false;
  proctoringState: ViolationState = { warningCount: 0, criticalCount: 0, isTerminated: false, currentWarning: null, terminationReason: null };
  hasConsent = false;
  hasScreenShared = false;
  isB2cPractice = false;
  networkQuality: string = 'CHECKING';
  private proctorSub?: Subscription;
  private connectivitySub?: Subscription;

  // private recognition?: any;
  // private sttActive = false;
  // private aiPartial = '';
  private timer?: any;
  private terminationPromptTriggered = false;
  private startTime = Date.now();
  private destroy$ = new Subject<void>();
  private lastSynced = 0;
  private syncDebounceTimer: any;
  private interviewEnded = false;
  isNavigating: boolean = false;
  private interviewFinalized = false;
  private autoScroll = true;
  private scrollThreshold = 120;
  private scrollPending = false;
  private handlingPop = false;
  private snapshotTimer?: any;
  private connectivityInterval?: any;
  sessionId?: number | string;
  organizationName: string = '';
  logoUrl: string | null = null;
  interviewStartedAt: string = '';
  private localStream?: MediaStream;
  private screenStream?: MediaStream;
  private tabViolationCount = 0;
  private isTabActive = true;
  private lastViolationTime = 0;
  private readonly VIOLATION_DEBOUNCE_MS = 3000;
  private boundBeforeUnload = (e: BeforeUnloadEvent) => this.onBeforeUnload(e);

  private mediaRecorder?: MediaRecorder;
  private recordedChunks: BlobPart[] = [];
  private recordingMimeType = '';
  private recordingStarted = false;
  private recordingStopped = false;

  /** Accumulates response.text.delta chunks for the current text-only response item */
  private textItemBuffer = '';
  /** The raw [VOICE_ANALYSIS_START]...[VOICE_ANALYSIS_END] block emitted by the Realtime model */
  private voiceAnalysisRaw = '';
  /** Tracks whether the one-time English communication reminder has been delivered */
  private languageWarningGiven = false;

  constructor(private toast: ToastrService,
    private webrtc: WebrtcService,
    public sessionService: AiInterviewSessionService,
    private router: Router,
    private route: ActivatedRoute,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private toastr: ToastrService,
    public proctoringService: ProctoringService,
    private connectivity: ConnectivityService,
    private dialog: MatDialog,
    private s3Uploader: S3MultipartUploaderService
  ) { }

  async ngOnInit() {
    const sessionIdParam = this.route.snapshot.paramMap.get('sessionId');
    this.sessionId = sessionIdParam ? (isNaN(Number(sessionIdParam)) ? sessionIdParam : Number(sessionIdParam)) : null as any;
    this.organizationName = sessionStorage.getItem('organization_name') || '';
    this.logoUrl = sessionStorage.getItem('logo_url');

    const sessionState = this.sessionService.value;
    this.isB2cPractice = this.route.snapshot.queryParamMap.get('is_b2c_practice') === 'true'
      || sessionStorage.getItem('is_b2c_practice') === 'true'
      || sessionStorage.getItem(`is_b2c_practice_${this.sessionId}`) === 'true'
      || sessionState?.is_b2c_practice === true;

    if (this.isB2cPractice) {
      this.hasConsent = true;
      this.hasScreenShared = true;
    } else {
      if (sessionState?.session_id) {
        this.proctoringService.setSessionId(sessionState.session_id);
      } else if (this.sessionId) {
        this.proctoringService.setSessionId(this.sessionId);
      }

      // Load proctoring config as early as possible
      this.proctoringService.initializeConfig('default').catch(() => { });
    }

    const savedViolations = sessionStorage.getItem(`violations_${this.sessionId}`);
    if (savedViolations) {
      this.tabViolationCount = Number(savedViolations);
    }

    const savedChat = sessionStorage.getItem(`chat_${this.sessionId}`);
    if (savedChat) {
      try {
        const parsed = JSON.parse(savedChat);
        this.chatLog = parsed.chatLog || [];
        this.transcript = parsed.transcript || [];
      } catch (e) {
        console.warn('Could not restore chat history');
      }
    }

    window.addEventListener('beforeunload', this.boundBeforeUnload);

    const isAnalyzing = sessionStorage.getItem(`interview_analyzing_${this.sessionId}`);
    if (isAnalyzing) {
      this.interviewEnded = true;
      this.interviewFinalized = true;
      this.toast.error('You cannot retake the interview. Your results are being processed.', 'Not Allowed', { disableTimeOut: true });
      this.router.navigateByUrl('/interview-completed');
      return;
    }

    const isActive = sessionStorage.getItem(`interview_active_${this.sessionId}`);
    if (isActive) {
      this.toast.error('Interview interrupted by page reload. The interview will submit automatically.', 'Interview Ended', { disableTimeOut: true });
      await this.autoSubmitInterview();
      return;
    }

    sessionStorage.setItem(`interview_active_${this.sessionId}`, 'true');

    if (!this.isB2cPractice) {
      this.proctorSub = this.proctoringService.violationState$.subscribe(state => {
        this.proctoringState = state;
        if (state.isTerminated && !this.interviewEnded) {
          this.interviewEnded = true; // prevent multiple triggers

          // Stop proctoring detection immediately to prevent further violation events.
          // Do NOT call forceStopRealtime() here — autoSubmitInterview() needs isConnected=true
          // so it can request the final voice analysis block from the Realtime model before closing.
          console.log('[InterviewPanel] Proctoring limit exceeded. Stopping detection; voice analysis will be captured before WebRTC teardown.');
          this.proctoringService.stopDetection();
          // Mute remote audio so the AI does not speak during the dialog
          const remoteAudioEl = this.remoteAudio?.nativeElement;
          if (remoteAudioEl) remoteAudioEl.muted = true;

          const dialogRef = this.dialog.open(ProctoringTerminatedComponent, {
            data: { reason: state.terminationReason },
            disableClose: true,
            panelClass: 'proctoring-terminated-dialog'
          });

          dialogRef.afterClosed().subscribe(() => {
            this.autoSubmitInterview();
          });
        }
      });
    }

    this.connectivitySub = this.connectivity.quality$.subscribe(q => {
      this.networkQuality = q;
      this.cdr.detectChanges();
    });

    // Start network health check loop
    this.connectivityInterval = setInterval(() => this.connectivity.checkQuality(), 10000);

    if (this.isB2cPractice) {
      this.initializeWebRTC();
    }
  }

  handleConsent(agreed: boolean) {
    if (!agreed) {
      this.router.navigateByUrl('/home');
      return;
    }
    this.hasConsent = true;
  }

  handleScreenShare(stream: MediaStream) {
    this.screenStream = stream;
    this.hasScreenShared = true;

    // Trap back navigation once the interview starts
    history.pushState({ interview: true }, '', location.href);

    this.screenStream.getVideoTracks()[0].onended = () => {
      if (!this.interviewEnded) {
        this.proctoringService.triggerViolation('SCREEN_SHARE_STOPPED', ViolationSeverity.CRITICAL);
        this.toastr.error('Screen sharing was stopped. This is required for proctoring.', 'Proctoring Error', { disableTimeOut: true });
        this.autoSubmitInterview();
      }
    };

    if (this.snapshotVideo) {
      this.snapshotVideo.nativeElement.srcObject = this.screenStream;
      this.proctoringService.setSnapshotSource(this.snapshotVideo.nativeElement);
    }

    this.initializeWebRTC();
  }

  handleScreenShareBack() {
    this.router.navigateByUrl('/home');
  }

  ngAfterViewInit() {
    if (!this.chatWindow) return;
    const chatEl = this.chatWindow.nativeElement;

    // Detect manual scroll up → disable auto-scroll
    chatEl.addEventListener('scroll', () => {
      const atBottom =
        chatEl.scrollHeight - chatEl.scrollTop - chatEl.clientHeight < this.scrollThreshold;
      this.autoScroll = atBottom;
    });
  }

  canExitInterview(): boolean {
    return this.interviewEnded || this.interviewFinalized;
  }

  /** Add message and auto-scroll safely */
  private appendMessage(role: 'assistant' | 'user', content: string) {
    this.ngZone.run(() => { // <-- ensures Angular knows about the change
      this.chatLog.push({ role, content, ts: new Date().toISOString() });

      if (this.sessionId) {
        sessionStorage.setItem(`chat_${this.sessionId}`, JSON.stringify({
          chatLog: this.chatLog,
          transcript: this.transcript
        }));
      }

      if (this.autoScroll) {
        if (!this.scrollPending) {
          this.scrollPending = true;
          requestAnimationFrame(() => {
            this.safeScrollToBottom();
            this.scrollPending = false;
          });
        }
      }
    });
  }

  private safeScrollToBottom() {
    if (!this.chatWindow) return;
    const chatEl = this.chatWindow.nativeElement;
    chatEl.scrollTo({ top: chatEl.scrollHeight, behavior: 'smooth' });
  }


  @HostListener('window:popstate', ['$event'])
  onPopState(event: PopStateEvent) {
    if (!this.hasConsent || !this.hasScreenShared) {
      // Allow navigation if interview hasn't started yet
      return;
    }

    if (this.handlingPop) return;

    this.handlingPop = true;

    history.pushState({ interview: true }, '', location.href);

    this.toastr.warning(
      '⚠️ You cannot go back during an active interview.',
      'Interview in Progress'
    );

    // allow future pop events after a short delay
    setTimeout(() => {
      this.handlingPop = false;
    }, 300);
  }

  @HostListener('window:blur')
  onWindowBlur() {
    this.handleTabViolation('Window blurred');
  }

  @HostListener('window:focus')
  onWindowFocus() {
    this.isTabActive = true;
    this.proctoringService.resetTabSwitchTimer();
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange() {
    if (document.hidden) {
      this.handleTabViolation('Tab switched');
    } else {
      this.isTabActive = true;
      this.proctoringService.resetTabSwitchTimer();
    }
  }

  private onBeforeUnload(e: BeforeUnloadEvent) {
    if (this.interviewEnded && !this.isAnalyzing) return;
    e.preventDefault();
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(e: KeyboardEvent) {
    if (this.interviewEnded) return;

    // Block F5
    if (e.key === 'F5') {
      e.preventDefault();
      e.stopPropagation();
      this.showLeaveConfirmation();
      return;
    }

    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'r') {
      e.preventDefault();
      e.stopPropagation();
      this.showLeaveConfirmation();
      return;
    }

    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'w') {
      e.preventDefault();
      e.stopPropagation();
      this.showLeaveConfirmation();
      return;
    }

    if (e.key === 'Escape' && this.showLeaveDialog) {
      e.preventDefault();
      this.dismissLeaveDialog();
      return;
    }
  }

  @HostListener('document:contextmenu', ['$event'])
  onContextMenu(e: MouseEvent) {
    if (this.interviewEnded) return;
    e.preventDefault();
  }

  /** Show the styled leave confirmation dialog */
  showLeaveConfirmation() {
    if (this.showLeaveDialog || this.interviewEnded) return;
    this.showLeaveDialog = true;
    this.cdr.detectChanges();
  }

  /** Dismiss the dialog — stay in interview */
  dismissLeaveDialog() {
    this.showLeaveDialog = false;
    this.cdr.detectChanges();
  }

  /** Confirm leave — auto-submit the interview */
  async confirmLeave() {
    this.showLeaveDialog = false;
    this.cdr.detectChanges();
    await this.autoSubmitInterview();
  }

  private handleTabViolation(reason: string) {
    if (this.isB2cPractice) return;
    if (this.interviewEnded || !this.isConnected || !this.isTabActive) return;

    const now = Date.now();
    if (now - this.lastViolationTime < this.VIOLATION_DEBOUNCE_MS) return;

    this.isTabActive = false;
    this.lastViolationTime = now;

    this.proctoringService.triggerViolation(
      ProctoringViolationType.TAB_SWITCH,
      ViolationSeverity.CRITICAL
    );
  }
  /** -------------------- WebRTC Setup -------------------- */
  private async initializeWebRTC() {
    try {
      const session = this.sessionService.value;
      if (!session?.ephemeral_token || !session?.openai_api_url) {
        this.toast.error('No active interview session. Please restart.');
        return;
      }

      const localStream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: { frameRate: { max: 10 }, width: { max: 640 }, height: { max: 480 } },
      });

      this.localStream = localStream;
      this.videoElement.nativeElement.srcObject = localStream;
      this.videoElement.nativeElement.muted = true;
      if (!this.isB2cPractice) {
        this.proctoringService.initAudioProctoring(localStream);
      }

      const webrtcPromise = this.webrtc.connect({
        webrtcConfig: session.webrtc_config!,
        apiUrl: session.openai_api_url,
        model: session.model || 'gpt-4o-mini-realtime-preview-2024-12-17',
        ephemeralToken: session.ephemeral_token,
        localStream,
        onTrack: (e) => {
          const [stream] = e.streams;
          this.remoteAudio.nativeElement.srcObject = stream;
          this.remoteAudio.nativeElement.volume = 0.8;
        },
        onMessage: (e: MessageEvent) => this.handleAIEvent(e.data),
      });

      let remoteStream: MediaStream | null = null;
      if (!this.isB2cPractice) {
        const modelsPromise = this.proctoringService.initializeModels().catch(err => {
          console.warn('[Proctoring] AI models failed to load:', err);
        });

        // We already have this.screenStream from the inline ScreenShareRequiredComponent
        const [rStream] = await Promise.all([webrtcPromise, modelsPromise]);
        remoteStream = rStream;

        const checkAndStart = () => {
          this.proctoringService.startDetection(
            this.videoElement.nativeElement,
            session.session_id || String(this.sessionId)
          );
        };

        if (this.videoElement.nativeElement.readyState >= 3) {
          checkAndStart();
        } else {
          this.videoElement.nativeElement.onloadeddata = () => checkAndStart();
        }
      } else {
        remoteStream = await webrtcPromise;
      }

      if (remoteStream) {
        this.remoteAudio.nativeElement.srcObject = remoteStream;
      }

      this.isConnected = true;
      this.interviewStartedAt = this.getFormattedDateTime();
      if (!this.isB2cPractice) {
        this.proctoringService.triggerViolation(ProctoringViolationType.SESSION_START, ViolationSeverity.INFO);
        this.startRecording();
      }
      this.startTimer();

      // 5. Inject mandatory-questions strategy so the model treats them as
      //    conversation topics rather than a sequential checklist.
      //       const mandatoryRaw = sessionStorage.getItem('mandatory_questions');
      //       if (mandatoryRaw) {
      //         try {
      //           const mandatoryQuestions: string[] = JSON.parse(mandatoryRaw);
      //           if (mandatoryQuestions.length > 0) {
      //             const questionList = mandatoryQuestions.map((q, i) => `${i + 1}. ${q}`).join('\n');
      //             this.webrtc.send({
      //               type: 'conversation.item.create',
      //               item: {
      //                 type: 'message',
      //                 role: 'system',
      //                 content: [{
      //                   type: 'input_text',
      //                   text: `[MANDATORY TOPIC GUIDANCE]\nYou must naturally cover each of the following topics during this interview. Treat them as conversation areas, NOT as a rigid question-by-question script:\n\n${questionList}\n\nGuidance:\n- Listen carefully to each response. If the candidate already covers a topic organically, do not re-ask it — move on.\n- Ask one or two thoughtful follow-up questions where appropriate before transitioning.\n- Once all mandatory topics are adequately covered, shift to questions based on the candidate's resume and the job description.\n- Maintain a professional, warm, conversational tone throughout. Never announce "I will now ask the next required question."`
      //                 }]
      //               }
      //             });
      //             console.log('[InterviewPanel] Mandatory questions strategy injected into session.');
      //           }
      //         } catch (e) {
      //           console.warn('[InterviewPanel] Could not parse mandatory_questions', e);
      //         }
      //       }

      //       // 6. If this is a teacher/educator role, inject supplementary persona guidance.
      //       //    The HRMS backend builds the base system prompt using is_teacher, but injecting
      //       //    from the frontend ensures the model prioritises educator-specific probing even
      //       //    if the backend prompt is generic.
      //       if (session.is_teacher) {
      //         this.webrtc.send({
      //           type: 'conversation.item.create',
      //           item: {
      //             type: 'message',
      //             role: 'system',
      //             content: [{
      //               type: 'input_text',
      //               text: `[EDUCATOR INTERVIEW GUIDANCE]
      // This is an interview for a teacher / educator role. Your questions must evaluate the candidate as a teaching professional, not as a generic subject-matter expert. Focus on:

      // • Teaching methodology — lesson planning, differentiated instruction, handling diverse learners and learning styles
      // • Subject expertise — depth of knowledge in their domain, ability to simplify complex concepts with analogies and real examples
      // • Classroom presence — student engagement strategies, motivation techniques, managing disruptions professionally
      // • Practical experience — institutions and grade levels taught, curriculum frameworks used, class sizes managed
      // • Reflective practice — how they assess student progress, handle underperformers, pursue continuous professional development
      // • Communication — clarity of explanation, ability to adapt language for different student levels

      // Ask natural follow-up questions that go beyond surface answers. A strong educator candidate gives concrete examples from real classroom situations, not hypothetical responses. Probe for specifics when answers are vague.`
      //             }]
      //           }
      //         });
      //         console.log('[InterviewPanel] Educator interview guidance injected into session.');
      //       }

      // 7. Send AI greeting immediately after screen share is confirmed
      // Greeting instruction is rendered by the backend from the selected language's config.
      // If it is missing, the session's system prompt defines the opening turn.
      const greetingInstruction = this.runtimeInstructions?.greeting;
      this.webrtc.send({
        type: 'response.create',
        response: {
          ...(greetingInstruction ? { instructions: greetingInstruction } : {}),
          output_modalities: ['audio'],
        },
      });

    } catch (err) {
      console.error('WebRTC init error:', err);
      this.toast.error('Failed to initialize AI Interview.');
    }
  }

  /** -------------------- Handle AI Events -------------------- */
  private isAiSpeaking = false;
  // private languageWarningGiven = false;

  private handleAIEvent(data: string) {
    try {
      const message = JSON.parse(data);
      switch (message.type) {

        case 'response.output_audio.delta': {
          if (!this.isAiSpeaking) {
            this.playDubbVideo();
            this.isAiSpeaking = true;
            this.muteMic(true);
            // Pause background-voice analysis while AI speaks — the raw mic stream
            // picks up AI room echo which corrupts the baseline and causes false positives.
            this.proctoringService.setAudioAnalysisPaused(true);
          }
          break;
        }

        case 'response.output_audio_transcript.done': {
          const transcript = message.transcript?.trim();
          if (transcript) {
            // Skip appending voice analysis report if emitted as an audio transcript
            if (transcript.includes('[VOICE_ANALYSIS_START]') || transcript.includes('VOICE_ANALYSIS_START')) {
              this.stopDubbVideo();
              this.isAiSpeaking = false;
              this.muteMic(false);
              this.proctoringService.setAudioAnalysisPaused(false);
              break;
            }

            const reminderAck = this.runtimeInstructions?.language_reminder_ack;
            if (reminderAck && !this.languageWarningGiven && this.isLanguageReminderPhrase(transcript)) {
              this.languageWarningGiven = true;
              console.log('[AI] Language reminder detected — injecting persistent system instruction to prevent repetition');
              this.sendSystemMessage(reminderAck);
            }
            this.appendMessage('assistant', transcript);
            this.transcript.push(`🤖 AI: ${transcript}`);
          }
          this.stopDubbVideo();
          this.isAiSpeaking = false;
          setTimeout(() => {
            this.muteMic(false);
            this.proctoringService.setAudioAnalysisPaused(false);
          }, 300);
          break;
        }

        case 'response.output_text.delta': {
          // The Realtime model emits the voice analysis JSON as a text-only (silent) response item.
          // Accumulate delta chunks until response.text.done fires.
          this.textItemBuffer += (message.delta ?? '');
          break;
        }

        case 'response.output_text.done': {
          // Full text-only item received. Check if it contains the voice analysis block.
          const textContent = this.textItemBuffer.trim();
          this.textItemBuffer = '';
          if (textContent.includes('[VOICE_ANALYSIS_START]') && textContent.includes('[VOICE_ANALYSIS_END]')) {
            this.voiceAnalysisRaw = textContent;
            console.log('[AI] Voice analysis block captured from Realtime text output.');
          }
          break;
        }

        case 'response.done': {
          // Safety net: always unmute and resume audio analysis when response completes,
          // even if audio was cancelled or truncated mid-sentence.
          this.stopDubbVideo();
          this.isAiSpeaking = false;
          this.muteMic(false);
          this.proctoringService.setAudioAnalysisPaused(false);
          break;
        }

        case 'error': {
          // Ensure mic is never left permanently muted after an AI error
          console.warn('[AI] Error event:', message.error);
          this.isAiSpeaking = false;
          this.muteMic(false);
          this.proctoringService.setAudioAnalysisPaused(false);
          break;
        }

        case 'conversation.item.input_audio_transcription.completed': {
          const transcript = message.transcript?.trim();
          if (!transcript) return;

          this.transcript.push(`🧑 You: ${transcript}`);
          this.appendMessage('user', transcript);
          // VAD handles response creation automatically — no response.create needed here.
          // Sending one caused a second AI response per turn (self-answering, repeated questions).
          break;
        }

        default:
          break;
      }
    } catch (error) {
      console.warn('AI Event Parsing Error:', error);
    }
  }

  // Detects whether the AI's transcript contains a language reminder, regardless of exact phrasing.
  private isLanguageReminderPhrase(transcript: string): boolean {
    const preferredLanguage = sessionStorage.getItem('preferred_interview_language') || this.sessionService.value?.preferred_language || 'en';
    if (preferredLanguage !== 'en') {
      return false; // No English reminder constraint for non-English interviews
    }
    const t = transcript.toLowerCase();
    return (
      ((t.includes('suggest') || t.includes('recommend') || t.includes('prefer')) && t.includes('english')) ||
      t.includes('communicate in english') ||
      t.includes('respond in english') ||
      t.includes('use english') ||
      t.includes('speak in english') ||
      t.includes('try to communicate in english') ||
      t.includes('i understand your point clearly')
    );
  }

  private playDubbVideo() {
    const videoEl = this.videoElement?.nativeElement;
    if (videoEl) videoEl.classList.add('ai-speaking');
  }

  private stopDubbVideo() {
    const videoEl = this.videoElement?.nativeElement;
    if (videoEl) videoEl.classList.remove('ai-speaking');
  }

  /** -------------------- Speech Recognition -------------------- */
  // private startLocalTranscription() {
  //   const w = window as any;
  //   const SR = w.SpeechRecognition || w.webkitSpeechRecognition;
  //   if (!SR) return;

  //   this.recognition = new SR();
  //   this.recognition.lang = 'en-IN';
  //   this.recognition.interimResults = true;
  //   this.recognition.continuous = true;

  //   this.recognition.onresult = (event: any) => {
  //     let finalText = '';
  //     for (let i = event.resultIndex; i < event.results.length; i++) {
  //       const text = event.results[i][0].transcript;
  //       if (event.results[i].isFinal) finalText += text;
  //     }
  //     if (finalText.trim()) {
  //       const clean = finalText.trim();
  //       this.transcript.push(`🧑 You: ${clean}`);
  //       this.chatLog.push({ role: 'user', content: clean, ts: new Date().toISOString() });

  //       this.webrtc.send({
  //         type: 'conversation.item.create',
  //         item: {
  //           type: 'message',
  //           role: 'user',
  //           content: [{ type: 'text', text: clean }],
  //         },
  //       });

  //       this.webrtc.send({ type: 'response.create' });
  //     }
  //   };

  //   this.recognition.onerror = (err: any) => {
  //     console.warn('SpeechRecognition error:', err.error);
  //   };
  //   this.recognition.onend = () => {
  //     if (this.sttActive) setTimeout(() => this.recognition.start(), 500);
  //   };

  //   this.sttActive = true;
  //   this.recognition.start();
  // }


  private syncConversationToServer() {
    if (this.interviewFinalized) return; // stop syncing after end

    const session = this.sessionService.value;
    if (!session.session_id || this.chatLog.length === 0) return;

    const now = Date.now();
    if (now - this.lastSynced < 3000) return;
    this.lastSynced = now;

    const payload: any = {
      session_id: session.session_id,
      conversation_json: this.chatLog.map((t) => ({
        role: t.role,
        content: t.content,
        timestamp: t.ts,
      })),
    };

    // Include voice analysis block if the Realtime model has already emitted it
    if (this.voiceAnalysisRaw) {
      payload.voice_analysis_raw = this.voiceAnalysisRaw;
    }

    this.sessionService.updateConversation(payload).subscribe({
      next: (res) => {
        if (!res.Success) console.warn('Sync failed:', res.Message);
      },
      error: (err) => {
        if (err.status === 401) {
          console.warn('Auth expired, skipping sync.');
        } else if (err.message?.includes('No primary')) {
        } else {
          console.error('Sync error:', err);
        }
      },
    });
  }

  private startTimer() {
    const session = this.sessionService.value;
    const totalMinutes = session.duration_minutes || 10;
    const totalSeconds = totalMinutes * 60;

    const savedStartTime = sessionStorage.getItem(`timer_${this.sessionId}`);
    if (savedStartTime) {
      this.startTime = Number(savedStartTime);
    } else {
      this.startTime = Date.now();
      sessionStorage.setItem(`timer_${this.sessionId}`, this.startTime.toString());
    }

    this.timer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - this.startTime) / 1000);
      const remaining = totalSeconds - elapsed;

      const warningSeconds = this.runtimeInstructions?.time_warning_seconds;
      if (warningSeconds != null && remaining <= warningSeconds && !this.terminationPromptTriggered && !this.interviewEnded) {
        this.terminationPromptTriggered = true;
        this.triggerTerminationWarning();
      }

      if (remaining <= 0 && !this.interviewEnded) {
        clearInterval(this.timer);
        this.interviewEnded = true;
        this.toast.info('⏳ Time is up! Submitting your interview automatically...');
        this.autoSubmitInterview();
      } else if (remaining > 0) {
        const min = Math.floor(remaining / 60);
        const sec = remaining % 60;
        this.timeRemaining = `${min}:${sec.toString().padStart(2, '0')}`;
        this.cdr.markForCheck();
      }
    }, 1000);
  }

  /** Prompt text rendered by the backend for this session (see InterviewRuntimeInstructions). */
  private get runtimeInstructions(): InterviewRuntimeInstructions | undefined {
    return this.sessionService.value?.runtime_instructions;
  }

  private sendSystemMessage(text: string) {
    this.webrtc.send({
      type: 'conversation.item.create',
      item: {
        type: 'message',
        role: 'system',
        content: [{ type: 'input_text', text }],
      },
    });
  }

  private triggerTerminationWarning() {
    const warning = this.runtimeInstructions?.time_warning;
    if (!warning) return;
    // Inject persistent context. We do not force response.cancel or response.create here,
    // so if the user is answering, the AI waits for them to complete before speaking this warning.
    this.sendSystemMessage(warning);
  }

  async exitInterview(): Promise<void> {
    this.showLeaveConfirmation();
  }


  private async autoSubmitInterview(): Promise<void> {
    this.interviewEnded = true;
    this.muteMic(true);

    if (this.isConnected && !this.voiceAnalysisRaw) {
      this.ngZone.run(() => {
        this.isAnalyzing = true;
        this.cdr.detectChanges();
      });

      try {
        console.log('[InterviewPanel] Requesting final voice analysis from Realtime model...');

        // 0. Cancel any active response to avoid 'conversation_already_has_active_response' error
        this.webrtc.send({ type: 'response.cancel' });
        await new Promise(resolve => setTimeout(resolve, 200));

        // Mute remote audio so the candidate doesn't hear any silent/voice output of the report
        if (this.remoteAudio?.nativeElement) {
          this.remoteAudio.nativeElement.muted = true;
          this.remoteAudio.nativeElement.volume = 0;
        }

        // Disable all audio tracks in the remote stream as an additional safety layer
        const remoteStream = this.remoteAudio?.nativeElement?.srcObject as MediaStream;
        if (remoteStream) {
          remoteStream.getAudioTracks().forEach(track => track.enabled = false);
        }

        // Configure the session to be text-only to strictly prevent audio generation
        this.webrtc.send({
          type: 'session.update',
          session: {
            modalities: ['text']
          }
        });

        // 1. Send system instruction requesting the analysis with the exact JSON schema
        //    so the model outputs a reliable, parseable block regardless of its system prompt.
        // //         const isTeacherSession = !!this.sessionService.value?.is_teacher;
        // //         const voiceAnalysisPrompt = isTeacherSession
        // //           ? `The interview is now officially over. Based on the candidate's vocal delivery, explanation style, and communication throughout this educator interview, output ONLY the following JSON block. Do not speak. Output nothing outside the markers.

        // // [VOICE_ANALYSIS_START]
        // // {"vocal_delivery":{"score":0,"feedback":""},"expression_clarity":{"score":0,"feedback":""},"structured_communication":{"score":0,"feedback":""},"instructional_clarity":{"score":0,"feedback":""}}
        // // [VOICE_ANALYSIS_END]

        // // Fill in each score (1–10) and a one-sentence feedback string based on your observations. instructional_clarity specifically measures how clearly and logically the candidate explained teaching concepts and examples.`
        // //           : `The interview is now officially over. Based on the candidate's vocal delivery throughout this interview, output ONLY the following JSON block. Do not speak. Output nothing outside the markers.

        // // [VOICE_ANALYSIS_START]
        // // {"vocal_delivery":{"score":0,"feedback":""},"expression_clarity":{"score":0,"feedback":""},"structured_communication":{"score":0,"feedback":""}}
        // // [VOICE_ANALYSIS_END]

        // Fill in each score (1–10) and a one-sentence feedback string based on your observations.`;
        const voiceAnalysisPrompt = this.runtimeInstructions?.voice_analysis_request;
        if (!voiceAnalysisPrompt) {
          throw new Error('Voice analysis request missing from session runtime instructions');
        }
        this.sendSystemMessage(voiceAnalysisPrompt);

        // 2. Force the model to respond with text only
        this.webrtc.send({
          type: 'response.create',
          response: {
            output_modalities: ['text']
          }
        });

        // 3. Wait for the handleAIEvent to capture response.text.done (up to 12 seconds)
        let checks = 0;
        while (!this.voiceAnalysisRaw && checks < 24) {
          await new Promise(resolve => setTimeout(resolve, 500));
          checks++;
        }

        console.log('[InterviewPanel] Finished waiting for voice analysis. Captured:', !!this.voiceAnalysisRaw);
      } catch (e) {
        console.warn('[InterviewPanel] Failed while requesting final voice analysis:', e);
      }
    }

    this.forceStopRealtime();
    await this.finishInterview(); // Final update done here
  }

  private handleScreenShareStop() {
    this.toastr.error(
      'Screen sharing stopped. The interview must be terminated for proctoring compliance.',
      'Proctoring Termination',
      { disableTimeOut: true }
    );
    this.autoSubmitInterview();
  }


  private audioContext?: AudioContext;

  private forceStopRealtime() {
    try {
      if (this.audioContext && this.audioContext.state !== 'closed') {
        this.audioContext.close().catch(() => { });
      }
      this.webrtc.close();
      this.isConnected = false;

      // Stop all local tracks (Camera/Mic)
      if (this.localStream) {
        this.localStream.getTracks().forEach(t => t.stop());
        this.localStream = undefined;
      }

      // Stop all screen share tracks
      if (this.screenStream) {
        this.screenStream.getTracks().forEach(t => t.stop());
        this.screenStream = undefined;
      }

      const videoEl = this.videoElement?.nativeElement;
      if (videoEl) videoEl.srcObject = null;

      const audioEl = this.remoteAudio?.nativeElement;
      if (audioEl) audioEl.srcObject = null;

      const snapEl = this.snapshotVideo?.nativeElement;
      if (snapEl) snapEl.srcObject = null;

    } catch (e) {
      console.warn('[Media] Cleanup failed:', e);
    }
  }
  private hasFinalUpdateRun = false;

  private async finishInterview(): Promise<void> {
    if (this.hasFinalUpdateRun) return;
    this.hasFinalUpdateRun = true;

    this.ngZone.run(() => {
      this.interviewEnded = true;
      this.interviewFinalized = true;
      this.isAnalyzing = true;
      this.cdr.detectChanges();
    });

    sessionStorage.removeItem(`timer_${this.sessionId}`);
    sessionStorage.removeItem(`chat_${this.sessionId}`);
    sessionStorage.removeItem(`violations_${this.sessionId}`);
    sessionStorage.removeItem(`interview_active_${this.sessionId}`);
    sessionStorage.removeItem('candidate_info');
    sessionStorage.removeItem('organization_name');
    sessionStorage.setItem(`interview_analyzing_${this.sessionId}`, 'true');

    // 1. Initial Cleanup
    try {
      clearTimeout(this.syncDebounceTimer);
      clearInterval(this.timer);
      this.destroy$.next();
      this.destroy$.complete();
      this.forceStopRealtime();
    } catch (e) {
    }

    const session = this.sessionService.value;
    if (!session?.session_id) {
      await this.stopAndUploadRecording();

      this.ngZone.run(() => {
        this.isAnalyzing = false;
        this.cdr.detectChanges();
      });
      this.router.navigateByUrl('/interview-completed');
      return;
    }

    try {
      // 2. Final Conversation Sync
      const finalPayload: any = {
        session_id: session.session_id,
        conversation_json: this.chatLog.map((t) => ({
          role: t.role,
          content: t.content,
          timestamp: t.ts,
        })),
      };

      // Include voice analysis block if captured from Realtime model's text output
      if (this.voiceAnalysisRaw) {
        finalPayload.voice_analysis_raw = this.voiceAnalysisRaw;
        console.log('[InterviewPanel] Sending voice analysis from Realtime model to backend.');
      } else {
        console.log('[InterviewPanel] No Realtime voice analysis block captured — backend will use transcript analysis fallback.');
      }

      await firstValueFrom(this.sessionService.updateConversation(finalPayload))
        .catch(err => console.error('[InterviewPanel] Final sync failed:', err));

      // 3. Mark Interview as Ended
      const interviewEndedAt = this.getFormattedDateTime();
      const endRes = await firstValueFrom(
        this.sessionService.endInterview({
          session_id: session.session_id,
          interview_started_at: this.interviewStartedAt,
          interview_ended_at: interviewEndedAt
        })
      ).catch(err => {
        return { Success: false };
      });

      if (!endRes?.Success) {
      }

      await this.stopAndUploadRecording();
      /*this.sessionService.evaluateInterview(session.session_id, this.interviewStartedAt, interviewEndedAt)
        .subscribe({
          next: () => { }
        });

      await new Promise(resolve => setTimeout(resolve, 5000));*/
      try {
        await firstValueFrom(
          this.sessionService.evaluateInterview(session.session_id)
        );
      } catch (err) {
        console.error('[InterviewPanel] Evaluation request failed:', err);
      }
    } catch (err) {
      console.error('[InterviewPanel] Unexpected error in finishInterview:', err);
    } finally {
      this.ngZone.run(() => {
        this.isAnalyzing = false;
        this.cdr.detectChanges();
        sessionStorage.removeItem(`interview_analyzing_${this.sessionId}`);
        this.router.navigateByUrl('/interview-completed').catch(err => {
          window.location.href = '/interview-completed';
        });
      });
    }
  }

  trackByIndex(index: number, _item: any): number {
    return index;
  }

  private cleanup() {
    this.destroy$.next();
    this.destroy$.complete();
    this.forceStopRealtime();

    try {
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop();
      }
    } catch { }

    this.proctoringService.stopDetection();
    this.proctorSub?.unsubscribe();
    this.connectivitySub?.unsubscribe();

    clearInterval(this.timer);
    clearInterval(this.snapshotTimer);
    clearInterval(this.connectivityInterval);
    clearTimeout(this.syncDebounceTimer);
    if (!this.isNavigating) this.sessionService.reset();
  }

  private startRecording(): void {
    if (this.recordingStarted) return;
    if (!this.screenStream) return;
    if (typeof MediaRecorder === 'undefined') return;

    const combinedTracks: MediaStreamTrack[] = [
      ...this.screenStream.getVideoTracks()
    ];

    try {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const dest = this.audioContext.createMediaStreamDestination();

      if (this.localStream && this.localStream.getAudioTracks().length > 0) {
        const localSource = this.audioContext.createMediaStreamSource(this.localStream);
        localSource.connect(dest);
      }

      const remoteStream = this.remoteAudio?.nativeElement?.srcObject as MediaStream;
      if (remoteStream && remoteStream.getAudioTracks().length > 0) {
        const remoteSource = this.audioContext.createMediaStreamSource(remoteStream);
        remoteSource.connect(dest);
      }

      combinedTracks.push(...dest.stream.getAudioTracks());
    } catch (e) {
      console.warn('Audio mixing failed, falling back to local audio only', e);
      if (this.localStream) {
        combinedTracks.push(...this.localStream.getAudioTracks());
      }
    }

    if (combinedTracks.length === 0) return;

    const combinedStream = new MediaStream(combinedTracks);

    const mimeTypes = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
      'video/mp4',
    ];
    this.recordingMimeType = mimeTypes.find(m => MediaRecorder.isTypeSupported(m)) ?? '';

    try {
      const options: MediaRecorderOptions = {
        ...(this.recordingMimeType ? { mimeType: this.recordingMimeType } : {}),
        videoBitsPerSecond: 150000,
        audioBitsPerSecond: 32000,
      };

      this.mediaRecorder = new MediaRecorder(combinedStream, options);
      this.recordedChunks = [];

      const sessionId = this.sessionService.value?.session_id;
      if (sessionId) {
        firstValueFrom(this.sessionService.initiateMultipartUpload(sessionId, this.recordingMimeType)).then(res => {
           if (res && res.Data) {
             this.uploadId = res.Data.upload_id;
             this.objectKey = res.Data.object_key;
           }
        }).catch(err => console.error('Failed to initiate multipart upload', err));
      }

      this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0) {
          this.chunkBuffer.push(event.data);
          this.currentBufferSize += event.data.size;
          
          if (this.currentBufferSize >= this.CHUNK_SIZE_THRESHOLD) {
             this.flushQueue = this.flushQueue.then(() => this.flushChunkBuffer(false));
          }
        }
      };

      this.mediaRecorder.onerror = () => { };

      // timeslice = 5000ms: flush chunks every 5s to protect against crash
      this.mediaRecorder.start(5000);
      this.recordingStarted = true;
      this.proctoringService.setVideoStartTime(Date.now());
    } catch { }
  }


  
  private async flushChunkBuffer(isFinal: boolean = false) {
    if (this.chunkBuffer.length === 0) return;
    const chunkToUpload = new Blob(this.chunkBuffer, { type: this.recordingMimeType });
    this.chunkBuffer = [];
    this.currentBufferSize = 0;
    
    const currentPartNumber = this.partNumber++;
    const sessionId = this.sessionService.value?.session_id;
    if (!sessionId) return;
    
    const cacheId = await this.s3Uploader.cacheChunk(sessionId, chunkToUpload, currentPartNumber);

    try {
      if (!this.uploadId || !this.objectKey) return;
      const res = await firstValueFrom(this.sessionService.getMultipartPartUrl(sessionId, this.uploadId, this.objectKey, currentPartNumber));
      const url = res.Data.presigned_url;
      const etag = await this.s3Uploader.uploadPartToS3(url, chunkToUpload);
      this.uploadedParts.push({ PartNumber: currentPartNumber, ETag: etag });
      await this.s3Uploader.removeCachedChunk(cacheId);
    } catch (e) {
      console.error('Failed to upload part, will remain in cache for retry', e);
    }
  }

  private async uploadCachedChunks(sessionId: string) {
    const cachedChunks = await this.s3Uploader.getCachedChunks(sessionId);
    for (const chunkData of cachedChunks) {
      try {
        if (!this.uploadId || !this.objectKey) continue;
        const res = await firstValueFrom(this.sessionService.getMultipartPartUrl(sessionId, this.uploadId, this.objectKey, chunkData.partNumber));
        const url = res.Data.presigned_url;
        const etag = await this.s3Uploader.uploadPartToS3(url, chunkData.chunk);
        
        // Remove from pending list if already there (shouldn't be due to IDB logic)
        this.uploadedParts.push({ PartNumber: chunkData.partNumber, ETag: etag });
        await this.s3Uploader.removeCachedChunk(chunkData.id);
      } catch (e) {
        console.error('Failed to retry cached chunk', e);
        throw e; // Rethrow to prevent completion if pieces are missing
      }
    }
  }

  private async stopAndUploadRecording(): Promise<void> {
    if (!this.recordingStarted || this.recordingStopped) return;
    this.recordingStopped = true;

    try {
      await new Promise<void>((resolve) => {
        if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
          resolve();
          return;
        }

        this.mediaRecorder.onstop = () => resolve();

        try {
          this.mediaRecorder.stop();
        } catch {
          resolve();
        }

        setTimeout(() => resolve(), 3000);
      });

      const sessionId = this.sessionService.value?.session_id;
      if (!sessionId) return;

      // Ensure any pending flushes are completed
      await this.flushQueue;
      
      // Flush the remaining buffer
      await this.flushChunkBuffer(true);
      
      // Upload any cached chunks that failed
      try {
        await this.uploadCachedChunks(sessionId);
      } catch (e) {
        console.error("Failed to upload all cached chunks. They will remain in IndexedDB.");
        return; // Don't complete the upload if chunks are missing
      }

      if (this.uploadId && this.objectKey) {
        // Sort parts just in case
        this.uploadedParts.sort((a, b) => a.PartNumber - b.PartNumber);
        
        await firstValueFrom(this.sessionService.completeMultipartUpload(sessionId, this.uploadId, this.objectKey, this.uploadedParts))
          .catch(err => console.error('Failed to complete multipart upload', err));
      }

    } catch (e) {
      console.error(e);
    } finally {
      this.forceStopRealtime();
    }
  }

  private muteMic(muted: boolean) {
    this.localStream?.getAudioTracks().forEach(t => t.enabled = !muted);
  }



  ngOnDestroy() {
    window.removeEventListener('beforeunload', this.boundBeforeUnload);
    this.cleanup();
  }

  private getFormattedDateTime(): string {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();

    let hours = now.getHours();
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';

    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const formattedHours = String(hours).padStart(2, '0');

    return `${day}-${month}-${year} ${formattedHours}:${minutes}:${seconds} ${ampm}`;
  }
}

